import { useState, useCallback } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { validateCSVColumns } from '../utils/helpers';
import {
  findHeaderRowIndex,
  rowsToObjects,
  stripLeadingTitleLines,
} from '../utils/uploadParsing';

const useCSVProcessor = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);

  const processFile = useCallback((file, onSuccess) => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    setDebugInfo(null);

    // Shared tail: validate required columns, then hand rows to the caller.
    const finishWithRows = (rowObjects, meta) => {
      const columns = Object.keys(rowObjects[0] || {});
      const validation = validateCSVColumns(columns);

      if (!validation.isValid) {
        setError(`Missing required columns: ${validation.missingColumns.join(', ')}`);
        setDebugInfo({
          foundColumns: validation.foundColumns,
          requiredColumns: ['Ad Name', 'Ad Spend', 'Pixel CV', 'ROAS or Pixel ROAS'],
          missingColumns: validation.missingColumns,
          preview: JSON.stringify(rowObjects[0], null, 2),
          ...meta,
        });
        setIsLoading(false);
        return;
      }

      setSuccess(`Successfully processed ${rowObjects.length} rows with ${columns.length} columns`);
      setDebugInfo({
        rowCount: rowObjects.length,
        columnCount: columns.length,
        columns,
        sampleRow: rowObjects[0],
        ...meta,
      });
      setIsLoading(false);
      onSuccess(rowObjects);
      setTimeout(() => setSuccess(null), 5000);
    };

    // Excel path (.xlsx/.xls) — Meta/Triple Whale exports arrive as xlsx with
    // a title row above the real header; locate the header row and skip it.
    if (/\.(xlsx|xls)$/i.test(file.name || '')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const workbook = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
            header: 1,
            raw: false, // formatted strings; the mapper strips $/, itself
            defval: '',
          });

          const headerIdx = findHeaderRowIndex(rows);
          if (headerIdx === -1) {
            setError(
              'Could not find the header row (needs "Ad Name" and "Ad Spend") in the first 10 rows of the Excel sheet.'
            );
            setDebugInfo({ sheetName, firstRows: rows.slice(0, 3) });
            setIsLoading(false);
            return;
          }

          const rowObjects = rowsToObjects(rows, headerIdx);

          // Guard against Excel date cells rendered in a locale format
          // ("7/26/26", "26/07/2026"): downstream parsing would silently
          // guess the wrong day or fall back to today. Meta exports store
          // dates as ISO text ("2026-07-26T00"); anything else is rejected
          // rather than mangled. (CSV path is unchanged — legacy formats
          // there predate this check.)
          const dated = rowObjects.filter((r) => String(r['Date'] ?? '').trim() !== '');
          const nonIso = dated.filter(
            (r) => !/^\d{4}-\d{2}-\d{2}/.test(String(r['Date']).trim())
          );
          if (dated.length > 0 && nonIso.length > 0) {
            setError(
              `This Excel file has ${nonIso.length} Date value(s) not in ISO format (YYYY-MM-DD...), e.g. "${String(nonIso[0]['Date']).trim()}". ` +
                'Importing would risk assigning data to the wrong day. Re-export with ISO dates, or export as CSV.'
            );
            setDebugInfo({ sheetName, sampleBadDates: nonIso.slice(0, 5).map((r) => r['Date']) });
            setIsLoading(false);
            return;
          }

          finishWithRows(rowObjects, {
            source: 'xlsx',
            sheetName,
            headerRowIndex: headerIdx,
          });
        } catch (err) {
          setError(`Excel parsing error: ${err.message}`);
          setIsLoading(false);
        }
      };
      reader.onerror = () => {
        setError('Could not read the Excel file.');
        setIsLoading(false);
      };
      reader.readAsArrayBuffer(file);
      return;
    }

    // CSV path. First, try to detect the file encoding and delimiter
    const reader = new FileReader();
    reader.onload = (e) => {
      // Some exports put a title line above the real CSV header — strip it,
      // or PapaParse would treat the title as the header row.
      const text = stripLeadingTitleLines(e.target.result);
      const preview = text.substring(0, 1000);
      
      // Try parsing with different configurations
      const parseConfigs = [
        { delimiter: ',', header: true },
        { delimiter: '\t', header: true },
        { delimiter: ';', header: true },
        { delimiter: '|', header: true }
      ];
      
      let bestConfig = null;
      let maxFields = 0;

      for (const config of parseConfigs) {
        const result = Papa.parse(preview, config);
        if (result.data.length > 0 && Object.keys(result.data[0]).length > maxFields) {
          maxFields = Object.keys(result.data[0]).length;
          bestConfig = config;
        }
      }
      
      if (!bestConfig) {
        setError('Could not detect CSV format. Please ensure the file is a valid CSV.');
        setIsLoading(false);
        return;
      }
      
      // Parse the full (title-stripped) text with the best configuration
      Papa.parse(text, {
        ...bestConfig,
        complete: (results) => {
          if (results.errors.length > 0) {
            setError(`CSV parsing error: ${results.errors[0].message}`);
            setDebugInfo({
              error: results.errors[0],
              preview: preview.substring(0, 500),
              detectedDelimiter: bestConfig.delimiter,
              rowCount: results.data.length,
              columnCount: results.data.length > 0 ? Object.keys(results.data[0]).length : 0
            });
            setIsLoading(false);
            return;
          }

          finishWithRows(results.data, {
            source: 'csv',
            delimiter: bestConfig.delimiter === '\t' ? 'Tab' : bestConfig.delimiter,
          });
        },
        error: (error) => {
          setError(`File reading error: ${error.message}`);
          setIsLoading(false);
        },
        skipEmptyLines: true,
        dynamicTyping: false, // Keep as strings to handle currency formatting
        delimitersToGuess: [',', '\t', '|', ';']
      });
    };
    
    reader.readAsText(file);
  }, []);

  const clearMessages = useCallback(() => {
    setError(null);
    setSuccess(null);
    setDebugInfo(null);
  }, []);

  return {
    isLoading,
    error,
    success,
    debugInfo,
    processFile,
    clearMessages
  };
};

export default useCSVProcessor; 